import { useEffect, useMemo, useRef, useState } from 'react';
import { AdminScreen, LoadOrError, adminErrorText, useResource } from './Overview';
import {
  Alert, Button, Card, EmptyState, Icon, Input, Modal, Spinner, Textarea, useToast
} from '../../components/ui';
import { api } from '../../lib/api';
import { usePageMeta } from '../../lib/seo';
import { THEMES, applyTheme, normalizeTheme, themeOption, type ThemeId } from '../../lib/theme';
import { tr } from '../../lib/i18n';
import { LanguageCard } from '../../components/LanguageSelect';

interface SettingsPayload {
  settings: Record<string, string>;
}

interface FieldSpec {
  key: string;
  label: string;
  hint: string;
  kind: 'text' | 'number' | 'textarea';
  max: number;
  validate?: (value: string) => string | null;
}

const FIELDS: FieldSpec[] = [
  {
    key: 'trial_days',
    label: 'Dias de teste gratuito',
    hint: 'Usado pelo backend para calcular o período de teste de novas assinaturas.',
    kind: 'number',
    max: 2,
    validate: (value) => {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0 || n > 90) return 'Informe um número inteiro entre 0 e 90.';
      return null;
    }
  },
  {
    key: 'annual_discount_percent',
    label: 'Desconto anual padrão (%)',
    hint: 'Referência exibida na página de preços; o valor efetivo de cada plano é editado em Planos.',
    kind: 'number',
    max: 2,
    validate: (value) => {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0 || n > 90) return 'Informe um número inteiro entre 0 e 90.';
      return null;
    }
  },
  {
    key: 'home_headline',
    label: 'Chamada da página inicial',
    hint: 'Texto principal exibido na home pública.',
    kind: 'textarea',
    max: 4000
  },
  {
    key: 'support_whatsapp',
    label: 'WhatsApp de suporte',
    hint: 'Número com DDI+DDD, ex.: 5541999998888. Vazio significa que o canal não está divulgado.',
    kind: 'text',
    max: 40
  },
  {
    key: 'support_email',
    label: 'E-mail de suporte',
    hint: 'Contato exibido nas páginas de ajuda e nos e-mails transacionais.',
    kind: 'text',
    max: 254
  },
  {
    key: 'pix_key',
    label: 'Chave Pix da plataforma',
    hint: 'E-mail, telefone (5541999998888), CPF/CNPJ ou EVP. Com a chave preenchida, a tela "Meu plano" passa a emitir cobrança com QR Code e copia e cola; vazia, a cobrança fica marcada como não configurada. Confirmar o recebimento continua sendo manual, em Ativações.',
    kind: 'text',
    max: 77,
    validate: (value) => {
      const v = value.trim();
      if (v === '') return null;
      if (/\s/.test(v)) return 'A chave Pix não pode ter espaços em branco.';
      if (!/^[A-Za-z0-9@._+\-]{6,77}$/.test(v)) return 'Chave inválida: use e-mail, telefone, CPF/CNPJ ou a chave EVP fornecida pelo banco.';
      if (/^[0-9]+$/.test(v) && (v.length < 11 || v.length > 14)) return 'Chave numérica deve ter de 11 a 14 dígitos (telefone com DDI, CPF ou CNPJ).';
      return null;
    }
  },
  {
    key: 'pix_merchant_name',
    label: 'Nome do recebedor no Pix',
    hint: 'Aparece no comprovante de quem paga. Sem acento, maiúsculas, até 25 caracteres.',
    kind: 'text',
    max: 25
  },
  {
    key: 'pix_merchant_city',
    label: 'Cidade do recebedor no Pix',
    hint: 'Também vai no código de barras. Sem acento, até 15 caracteres.',
    kind: 'text',
    max: 15
  }
];

const KNOWN_KEYS = new Set([...FIELDS.map((f) => f.key), 'site_theme']);

export function AdminSettingsPage(): JSX.Element {
  usePageMeta('Configurações do site — administração', 'Parâmetros globais da plataforma AgendaPro.', '/admin/configuracoes');

  const toast = useToast();
  const resource = useResource<SettingsPayload>(() => api.get<SettingsPayload>('/api/admin/settings'));
  const settings = resource.data?.settings ?? {};

  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [extraKey, setExtraKey] = useState('');
  const [extraValue, setExtraValue] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [removeKey, setRemoveKey] = useState<string | null>(null);

  const current = draft ?? settings;
  const currentThemeId = normalizeTheme(current.site_theme);
  const savedThemeId = normalizeTheme(settings.site_theme);
  const persistedTheme = useRef<ThemeId | null>(null);

  useEffect(() => {
    if (resource.data) persistedTheme.current = savedThemeId;
  }, [resource.data, savedThemeId]);

  // Prévia não salva não pode vazar para as outras telas ao sair das configurações.
  useEffect(
    () => () => {
      if (persistedTheme.current) applyTheme(persistedTheme.current, false);
    },
    []
  );

  const changes = useMemo(() => {
    const base = resource.data?.settings ?? {};
    const out: { key: string; from: string; to: string }[] = [];
    for (const [key, value] of Object.entries(current)) {
      if ((base[key] ?? '') !== value) out.push({ key, from: base[key] ?? '(ausente)', to: value });
    }
    return out;
  }, [current, resource.data]);

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    for (const field of FIELDS) {
      const value = current[field.key] ?? '';
      if (field.validate && value.trim()) {
        const message = field.validate(value.trim());
        if (message) errors[field.key] = message;
      }
    }
    return errors;
  }, [current]);

  function update(key: string, value: string): void {
    setDraft((prev) => ({ ...(prev ?? settings), [key]: value }));
  }

  function previewTheme(id: ThemeId): void {
    setDraft((prev) => ({ ...(prev ?? settings), site_theme: id }));
    applyTheme(id, false);
  }

  function discardDraft(): void {
    setDraft(null);
    setSaveError(null);
    if (persistedTheme.current) applyTheme(persistedTheme.current, false);
  }

  function addExtra(): void {
    const key = extraKey.trim();
    if (!/^[a-z0-9_]{2,60}$/.test(key)) {
      toast('Use uma chave de 2 a 60 caracteres, minúscula, com números ou underline.', 'danger');
      return;
    }
    setDraft((prev) => ({ ...(prev ?? settings), [key]: extraValue }));
    setExtraKey('');
    setExtraValue('');
  }

  async function save(): Promise<void> {
    if (Object.keys(fieldErrors).length > 0) {
      setSaveError('Corrija os campos inválidos antes de salvar.');
      return;
    }
    if (changes.length === 0) {
      toast('Nada para salvar: nenhuma configuração foi alterada.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: Record<string, string> = {};
      for (const change of changes) payload[change.key] = change.to.trim();
      await api.put<{ ok: boolean }>('/api/admin/settings', { settings: payload });
      const themeChange = changes.find((change) => change.key === 'site_theme');
      if (themeChange) persistedTheme.current = applyTheme(themeChange.to, true);
      toast('Configurações gravadas e registradas em Auditoria (admin.settings_updated).', 'success');
      setConfirmOpen(false);
      setDraft(null);
      await resource.reload();
    } catch (e) {
      setSaveError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setSaving(false);
    }
  }

  const extraKeys = Object.keys(current).filter((key) => !KNOWN_KEYS.has(key));

  return (
    <AdminScreen
      title={tr("Configurações do site")}
      description={tr("Parâmetros globais gravados em <site_settings>. A API grava texto livre por chave (até 4000 caracteres), valida o tema contra as opções disponíveis e registra a lista de chaves alteradas na auditoria.")}
    >
      <LanguageCard />

      <LoadOrError loading={resource.loading} error={resource.error} />

      {resource.data ? (
        <Card className="mb-4">
          <h2 className="text-xl">{tr("Tema padrão da plataforma")}</h2>
          <p className="nl-hint mt-1">
            {tr("Vale para o site público, a busca, os perfis de agendamento, a administração e as lojas que ainda não escolheram um tema em Painel › Configurações › Aparência da loja. A prévia é imediata neste navegador; os visitantes passam a ver o tema depois de")} <strong>{tr("Salvar configurações")}</strong>.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {THEMES.map((theme) => {
              const selected = currentThemeId === theme.id;
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => previewTheme(theme.id)}
                  aria-pressed={selected}
                  style={{ borderColor: selected ? theme.swatch.accent : undefined }}
                  className={`relative overflow-hidden rounded-2xl border-2 bg-card text-left transition-all duration-200 ${
                    selected
                      ? 'shadow-[0_16px_30px_-18px_rgba(16,26,36,0.7)]'
                      : 'border-transparent ring-1 ring-line hover:ring-[var(--nl-border-strong)]'
                  }`}
                >
                  <span className="relative block h-16 w-full" style={{ background: theme.gradient }} aria-hidden="true">
                    <span className="absolute left-2.5 top-2.5 rounded-full bg-black/20 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
                      {tr(theme.group)}
                    </span>
                    <span className="absolute bottom-2.5 left-2.5 h-5 w-12 rounded-full bg-white/85" />
                    <span className="absolute bottom-2.5 left-[4.1rem] h-5 w-5 rounded-full bg-white/40" />
                    {selected ? (
                      <span
                        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm"
                        style={{ color: theme.swatch.accent }}
                      >
                        <Icon name="check" size={14} />
                      </span>
                    ) : null}
                  </span>
                  <span className="block px-3 pb-3 pt-2">
                    <span className="block font-semibold">{tr(theme.name)}</span>
                    <span className="nl-hint mt-0.5 block">{tr(theme.description)}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="nl-hint mt-3">
            {tr("Tema em uso:")} <strong>{themeOption(currentThemeId).name}</strong>
            {currentThemeId === savedThemeId
              ? '.'
              : ' — prévia ainda não salva. Clique em Salvar configurações ou em Descartar rascunho.'}
          </p>
        </Card>
      ) : null}

      {resource.data ? (
        <Card className="mb-4">
          <h2 className="text-xl">{tr("Parâmetros principais")}</h2>
          <div className="mt-4 grid gap-4">
            {FIELDS.map((field) => {
              const value = current[field.key] ?? '';
              const error = fieldErrors[field.key] ?? null;
              return (
                <div key={field.key}>
                  <label className="nl-label" htmlFor={`setting-${field.key}`}>
                    {tr(field.label)} <code className="text-xs font-normal text-muted">({field.key})</code>
                  </label>
                  {field.kind === 'textarea' ? (
                    <Textarea
                      id={`setting-${field.key}`}
                      rows={3}
                      maxLength={Math.min(field.max, 1000)}
                      value={value}
                      className={error ? 'border-danger' : ''}
                      aria-invalid={Boolean(error) || undefined}
                      onChange={(e) => update(field.key, e.target.value)}
                    />
                  ) : (
                    <Input
                      id={`setting-${field.key}`}
                      inputMode={field.kind === 'number' ? 'numeric' : 'text'}
                      maxLength={field.max}
                      value={value}
                      invalid={Boolean(error)}
                      onChange={(e) => update(field.key, field.kind === 'number' ? e.target.value.replace(/\D/g, '') : e.target.value)}
                    />
                  )}
                  <p className="nl-hint mt-1">{error ?? tr(field.hint)}</p>
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      {resource.data ? (
        <Card className="mb-4">
          <h2 className="text-xl">{tr("Outras chaves gravadas")}</h2>
          <p className="nl-hint mt-1">
            {tr("Qualquer chave pode ser criada por aqui — a API aceita um registro livre de texto.")}
            {extraKeys.length === 0 ? ' ' + tr('Nenhuma chave além das principais está registrada.') : ''}
          </p>
          {extraKeys.length > 0 ? (
            <ul className="mt-4 grid gap-2">
              {extraKeys.map((key) => (
                <li key={key} className="grid gap-2 rounded-md border border-line px-3 py-2 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] sm:items-end">
                  <div>
                    <span className="nl-label mb-1">{key}</span>
                    <Input
                      aria-label={`Valor de ${key}`}
                      value={current[key] ?? ''}
                      maxLength={4000}
                      onChange={(e) => update(key, e.target.value)}
                    />
                  </div>
                  <div>
                    <Button
                      variant="ghost"
                      className="px-3 py-1.5 text-xs"
                      disabled={saving}
                      onClick={() => setRemoveKey(key)}
                    >
                      <Icon name="close" size={16} /> {tr("Remover do rascunho")}
                    </Button>
                  </div>
                  <span className="nl-hint">{tr("Editável; só é gravada ao salvar.")}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-4 grid gap-2 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto] sm:items-end">
            <div>
              <label className="nl-label" htmlFor="extra-key">{tr("Nova chave")}</label>
              <Input
                id="extra-key"
                value={extraKey}
                maxLength={60}
                placeholder={tr("ex.: maintenance_message")}
                onChange={(e) => setExtraKey(e.target.value.toLowerCase())}
              />
            </div>
            <div>
              <label className="nl-label" htmlFor="extra-value">{tr("Valor")}</label>
              <Input id="extra-value" value={extraValue} maxLength={4000} onChange={(e) => setExtraValue(e.target.value)} />
            </div>
            <Button variant="secondary" disabled={saving} onClick={addExtra}>
              <Icon name="plus" /> {tr("Adicionar chave")}
            </Button>
          </div>
        </Card>
      ) : null}

      {resource.data ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="nl-hint">
            {changes.length === 0
              ? tr('Sem alterações pendentes.')
              : `${changes.length} ${changes.length === 1 ? 'chave alterada' : 'chaves alteradas'}: ${changes.map((c) => c.key).join(', ')}.`}
          </p>
          <div className="flex gap-2">
            {draft ? (
              <Button variant="ghost" disabled={saving} onClick={discardDraft}>
                {tr("Descartar rascunho")}
              </Button>
            ) : null}
            <Button
              disabled={saving || changes.length === 0}
              onClick={() => {
                setSaveError(null);
                setConfirmOpen(true);
              }}
            >
              {tr("Salvar configurações")}
            </Button>
          </div>
        </div>
      ) : null}

      {saveError ? <div className="mt-4"><Alert tone="danger" title={tr("Não foi possível salvar")}>{saveError}</Alert></div> : null}

      <Modal
        open={confirmOpen}
        onClose={() => (saving ? undefined : setConfirmOpen(false))}
        title={tr("Confirmar gravação das configurações")}
        footer={
          <>
            <Button variant="ghost" disabled={saving} onClick={() => setConfirmOpen(false)}>{tr("Voltar")}</Button>
            <Button loading={saving} disabled={saving} onClick={() => void save()}>
              {tr("Gravar agora")}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Alert tone="warning" title={tr("Efeito em toda a plataforma")}>
            {tr("Estes valores passam a valer imediatamente para todas as telas públicas e para o backend. Não há versão anterior guardada: para desfazer, grave os valores antigos manualmente. A gravação é registrada em Auditoria.")}
          </Alert>
          <ul className="grid gap-2">
            {changes.map((change) => (
              <li key={change.key} className="rounded-md bg-surface px-3 py-2 text-sm">
                <p className="font-semibold">{change.key}</p>
                <p className="nl-hint break-all">{tr("De:")} {change.from}</p>
                <p className="nl-hint break-all">{tr("Para:")} {change.to}</p>
              </li>
            ))}
          </ul>
          {saving ? <Spinner label={tr("Gravando")} /> : null}
        </div>
      </Modal>

      <Modal
        open={removeKey !== null}
        onClose={() => setRemoveKey(null)}
        title={tr("Remover chave do rascunho")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoveKey(null)}>{tr("Manter")}</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (removeKey) {
                  setDraft((prev) => {
                    const next = { ...(prev ?? settings) };
                    delete next[removeKey];
                    return next;
                  });
                  toast('Chave retirada do rascunho. Para apagar do banco, remova-a pela rota de configurações do backend — a API não tem DELETE de configuração.', 'info');
                }
                setRemoveKey(null);
              }}
            >
              {tr("Remover")}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p>
            {tr("A chave")} <code>{removeKey}</code> {tr("sai do rascunho desta tela.")}{' '}
            <strong>{tr("Ela continua gravada no banco")}</strong>: <code>{tr("PUT /api/admin/settings")}</code> {tr("só insere/atualiza, não existe rota de exclusão de configuração.")}
          </p>
          {Object.keys(settings).length === 0 ? <EmptyState title={tr("Nenhuma configuração carregada")} /> : null}
        </div>
      </Modal>
    </AdminScreen>
  );
}
